import React, { useMemo } from "react"
import dayjs from "dayjs"
import { Tooltip } from "antd"
import {
  LeaveTimelineBar,
  LeaveTimelineBarKind,
  LeaveTimelineDayFlag,
  LeaveTimelineRow,
} from "./types"
import {
  useTimelineDays,
  usePositionedBars,
  TimelineDay,
} from "./useTimelineDays"

const NAME_COLUMN_WIDTH = 200
const DAY_COLUMN_WIDTH = 28
const ROW_HEIGHT = 40

interface LeaveTimelineProps {
  rows: LeaveTimelineRow[]
  bars: LeaveTimelineBar[]
  /** ISO yyyy-MM-dd, inclusive. */
  startDate: string
  /** ISO yyyy-MM-dd, inclusive. */
  endDate: string
  dayFlags?: LeaveTimelineDayFlag[]
  loading?: boolean
  emptyMessage?: string
  onBarClick?: (bar: LeaveTimelineBar) => void
  /** Shown above the grid when a threshold applies, so a flagged column reads as deliberate. */
  thresholdLabel?: string
}

/**
 * People down, days across, absences as bars.
 *
 * Built as a CSS grid rather than adopting a scheduler library: the requirement is a fixed
 * matrix of rows by days with positioned bars, which is less work to write than to configure
 * and theme, and it stays inside the existing design system.
 *
 * Serves three surfaces — the calendar's quarter view, the overlap panel on an approval, and
 * the departmental planning roster. They differ in what they pass, not in how it is drawn.
 */
export const LeaveTimeline: React.FC<LeaveTimelineProps> = ({
  rows,
  bars,
  startDate,
  endDate,
  dayFlags = [],
  loading = false,
  emptyMessage = "Nobody is scheduled to be away in this period.",
  onBarClick,
  thresholdLabel,
}) => {
  const days = useTimelineDays(startDate, endDate, dayFlags)
  const barsByRow = usePositionedBars(bars, days)

  const monthSpans = useMemo(() => buildMonthSpans(days), [days])

  const gridTemplateColumns = `${NAME_COLUMN_WIDTH}px repeat(${days.length}, ${DAY_COLUMN_WIDTH}px)`

  if (!loading && (days.length === 0 || rows.length === 0)) {
    return (
      <div className="border border-neutral40 rounded-[5px] bg-white p-8 text-center">
        <p className="font-avenir text-sm text-padeSubtext">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="border border-neutral40 rounded-[5px] bg-white">
      {thresholdLabel ? (
        <div className="px-4 py-2 border-b border-neutral40 flex items-center gap-4 flex-wrap">
          <span className="font-avenir text-xs text-padeSubtext">
            {thresholdLabel}
          </span>
          <Legend
            bars={bars}
            showThreshold
          />
        </div>
      ) : (
        <div className="px-4 py-2 border-b border-neutral40">
          <Legend
            bars={bars}
            showThreshold={dayFlags.some((flag) => flag.exceedsThreshold)}
          />
        </div>
      )}

      {/* A quarter is ninety-odd columns and the grid clips at the card edge with no visible
          scrollbar on macOS, so it reads as "the data stops in July" unless we say otherwise. */}
      {days.length > 45 ? (
        <div className="px-4 py-1.5 border-b border-neutral40 bg-[#F8FAFC]">
          <span className="font-avenir text-[11px] text-padeSubtext">
            Showing {days.length} days — scroll sideways for the rest of the period. Names stay in view.
          </span>
        </div>
      ) : null}

      {/* The grid is the only thing that scrolls sideways, so the page never does. */}
      <div className="overflow-x-auto">
        <div
          style={{
            minWidth: NAME_COLUMN_WIDTH + days.length * DAY_COLUMN_WIDTH,
          }}
        >
          {/* Month band */}
          <div
            className="grid"
            style={{ gridTemplateColumns }}
          >
            <div className="sticky left-0 z-20 bg-white border-b border-r border-neutral40" />
            {monthSpans.map((month, monthIndex) => (
              <div
                key={month.key}
                style={{ gridColumn: `span ${month.span}` }}
                className={`border-b border-r border-neutral40 px-2 py-1 font-circular text-xs font-semibold text-padeBlack whitespace-nowrap overflow-hidden ${
                  // Alternating tone: across a quarter the columns are identical and a month
                  // boundary is otherwise just one more vertical line among ninety.
                  monthIndex % 2 === 1 ? "bg-[#F6F8FB]" : "bg-white"
                }`}
              >
                {month.label} <span className="text-padeSubtext font-normal">· {month.span} days</span>
              </div>
            ))}
          </div>

          {/* Day band */}
          <div
            className="grid"
            style={{ gridTemplateColumns }}
          >
            <div className="sticky left-0 z-20 bg-white border-b border-r border-neutral40 px-3 py-1 font-avenir text-xs text-padeSubtext">
              Employee
            </div>
            {days.map((day) => (
              <DayHeader
                key={day.key}
                day={day}
              />
            ))}
          </div>

          {/* Body.
              One grid for every row rather than a grid per row, with each element placed at
              an explicit gridRow. Per-row grids drifted out of alignment: bars are extra
              children, and any that a browser placed outside the single implicit row grew
              that row, so the name column and the day columns stopped agreeing on where a
              row ended. Explicit placement makes alignment structural. */}
          {loading ? (
            <SkeletonRows
              columns={gridTemplateColumns}
              dayCount={days.length}
            />
          ) : (
            <div
              className="grid"
              style={{ gridTemplateColumns, gridAutoRows: `${ROW_HEIGHT}px` }}
            >
              {rows.map((row, rowIndex) => (
                <React.Fragment key={row.id}>
                  <div
                    style={{ gridRow: rowIndex + 1, gridColumn: 1 }}
                    className="sticky left-0 z-10 bg-white border-b border-r border-neutral40 px-3 flex flex-col justify-center overflow-hidden"
                  >
                    <span className="font-avenir text-xs text-padeBlack truncate">
                      {row.name}
                    </span>
                    {row.subtitle ? (
                      <span className="font-avenir text-[10px] text-padeSubtext truncate">
                        {row.subtitle}
                      </span>
                    ) : null}
                  </div>

                  {days.map((day, dayIndex) => (
                    <div
                      key={day.key}
                      style={{
                        gridRow: rowIndex + 1,
                        gridColumn: dayIndex + 2,
                      }}
                      className={`border-b border-r border-neutral40 ${cellTone(
                        day
                      )}`}
                    />
                  ))}

                  {(barsByRow.get(row.id) ?? []).map((bar) => (
                    <div
                      key={bar.id}
                      style={{
                        gridRow: rowIndex + 1,
                        gridColumn: `${bar.columnStart + 1} / span ${bar.span}`,
                      }}
                      className="flex items-center px-[2px] py-[7px] pointer-events-none"
                    >
                      <Tooltip title={bar.label}>
                        <button
                          type="button"
                          onClick={
                            onBarClick ? () => onBarClick(bar) : undefined
                          }
                          aria-label={bar.label ?? "Leave"}
                          className={[
                            "w-full h-full rounded-[4px] pointer-events-auto",
                            "flex items-center px-2 overflow-hidden",
                            "shadow-[0_1px_2px_rgba(16,24,40,0.10)]",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary500",
                            onBarClick ? "cursor-pointer hover:brightness-95" : "cursor-default",
                            barTone(bar.kind),
                            bar.clippedStart ? "rounded-l-none" : "",
                            bar.clippedEnd ? "rounded-r-none" : "",
                          ].join(" ")}
                        >
                          {/* Only from four days wide: any narrower and the text is clipped
                              mid-word, which reads worse than no text at all. */}
                          {bar.span >= 4 ? (
                            <span className="font-avenir text-[10px] text-white truncate">
                              {bar.label}
                            </span>
                          ) : null}
                        </button>
                      </Tooltip>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const DayHeader: React.FC<{ day: TimelineDay }> = ({ day }) => {
  const isToday = day.date.isSame(dayjs(), "day")

  return (
  <div
    className={[
      "border-b border-r border-neutral40 text-center py-1 leading-tight",
      day.exceedsThreshold
        ? "bg-[#fdecea]"
        : day.nonWorking
        ? "bg-[#eef2f6]"
        : "",
      isToday ? "bg-primary50" : "",
    ].join(" ")}
  >
    <div className={`font-avenir text-[10px] ${day.nonWorking ? "text-neutral70" : "text-padeSubtext"}`}>
      {day.date.format("dd").charAt(0)}
    </div>
    <div
      className={`font-circular text-[11px] ${
        day.exceedsThreshold
          ? "text-[#b42318]"
          : isToday
            ? "text-primary500 font-semibold"
            : "text-padeBlack"
      }`}
    >
      {day.date.format("D")}
    </div>
  </div>
  )
}

const KIND_LABELS: { kind: LeaveTimelineBarKind; label: string }[] = [
  { kind: "approved", label: "Approved" },
  { kind: "pending", label: "Pending" },
  { kind: "planned", label: "Planned" },
  { kind: "focus", label: "Needs attention" },
]

/**
 * Only what is actually on the grid.
 *
 * A fixed legend describes the component rather than the data, so the planning roster ends
 * up advertising "Approved" and "Over threshold" when it can show neither. Deriving it means
 * each surface explains itself without any of them configuring a legend.
 */
const Legend: React.FC<{
  bars: LeaveTimelineBar[]
  showThreshold: boolean
}> = ({ bars, showThreshold }) => {
  const present = new Set(bars.map((bar) => bar.kind))
  const items = KIND_LABELS.filter((item) => present.has(item.kind))

  if (items.length === 0 && !showThreshold) {
    return null
  }

  return (
    <div className="flex items-center gap-4 flex-wrap font-avenir text-[11px] text-padeSubtext">
      {items.map((item) => (
        <LegendItem
          key={item.kind}
          className={barTone(item.kind)}
          label={item.label}
        />
      ))}
      {showThreshold ? (
        <LegendItem
          className="bg-[#fdecea] border border-[#f3b4ae]"
          label="Over threshold"
        />
      ) : null}
    </div>
  )
}

const LegendItem: React.FC<{ className: string; label: string }> = ({
  className,
  label,
}) => (
  <span className="flex items-center gap-1.5">
    <span className={`inline-block w-4 h-3 rounded-[2px] ${className}`} />
    {label}
  </span>
)

const SkeletonRows: React.FC<{ columns: string; dayCount: number }> = ({
  columns,
  dayCount,
}) => (
  <>
    {Array.from({ length: 6 }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        className="grid animate-pulse"
        style={{ gridTemplateColumns: columns, height: ROW_HEIGHT }}
      >
        <div className="sticky left-0 z-10 bg-white border-b border-r border-neutral40 px-3 flex items-center">
          <span className="h-3 w-24 rounded bg-neutral40" />
        </div>
        {Array.from({ length: dayCount }).map((_, dayIndex) => (
          <div
            key={dayIndex}
            className="border-b border-r border-neutral40"
          />
        ))}
      </div>
    ))}
  </>
)

function cellTone(day: TimelineDay): string {
  if (day.exceedsThreshold) return "bg-[#fdecea]"
  if (day.nonWorking) return "bg-[#eef2f6]"
  return ""
}

function barTone(kind: LeaveTimelineBar["kind"]): string {
  switch (kind) {
    case "approved":
      return "bg-[#2c6b5e]"
    case "pending":
      return "bg-[#7fb3a8]"
    case "planned":
      // Hatched rather than a flat tint: a plan is an intention and should not read as a
      // booking at a glance (AL-03 in visual form).
      return "bg-[repeating-linear-gradient(45deg,#c7d7d3_0px,#c7d7d3_3px,#eef3f2_3px,#eef3f2_6px)]"
    case "focus":
      return "bg-[#a33b22]"
    default:
      return "bg-neutral40"
  }
}

function buildMonthSpans(days: TimelineDay[]) {
  const spans: { key: string; label: string; span: number }[] = []

  days.forEach((day) => {
    const key = day.date.format("YYYY-MM")
    const last = spans[spans.length - 1]

    if (last && last.key === key) {
      last.span += 1
    } else {
      spans.push({ key, label: day.date.format("MMMM YYYY"), span: 1 })
    }
  })

  return spans
}

export default LeaveTimeline
